import { computed, ref, watch } from 'vue'
import { sanitizeLayout } from 'widget-components/utilities/sanitize'
import useWidgetOptions from '@/composables/widget-options/useWidgetOptions'
import { logger } from '@sentry/vue'

const isAssetUrl = (value: string) => /^https?:\/\//i.test(value.trim())

const layoutHtml = ref('')

const layoutPending = ref(false)

// guards against an earlier fetch resolving after a later one and overwriting it
let pendingRequest = 0

const resolveLayout = async (source: string) => {
    const request = ++pendingRequest

    if (!source) {
        layoutHtml.value = ''
        layoutPending.value = false
        return
    }

    if (!isAssetUrl(source)) {
        layoutHtml.value = sanitizeLayout(source)
        layoutPending.value = false
        return
    }

    layoutPending.value = true

    try {
        const response = await fetch(source)

        if (!response.ok) {
            throw new Error(`Explicit start layout responded with ${response.status}`)
        }

        const html = sanitizeLayout(await response.text())

        if (request === pendingRequest) {
            layoutHtml.value = html
        }
    } catch (e: unknown) {
        // an unreachable layout falls back to the default start screen - the workflow still starts
        logger.error('Cannot load explicit start layout', { error: e })

        if (request === pendingRequest) {
            layoutHtml.value = ''
        }
    } finally {
        if (request === pendingRequest) {
            layoutPending.value = false
        }
    }
}

function useExplicitStartLayout() {
    const { options } = useWidgetOptions()

    const source = computed(() => (
        options.value.explicitStart && options.value.explicitStartLayout || ''
    ))

    watch(source, resolveLayout, { immediate: true })

    return {
        layoutHtml: computed(() => layoutHtml.value),
        layoutPending: computed(() => layoutPending.value),
    }
}

export default useExplicitStartLayout
